import { formatDateLocal, getTodayLocal } from "../../date-utils";
import { buildContextualHints } from "./contextualHints";

const JSON_INDENT = 2;

const LANGUAGE_NAMES: Record<string, string> = {
  fr: "French",
  en: "English",
  es: "Spanish",
};

/**
 * Directive imposant la langue de SORTIE du contenu généré (titre, description,
 * options). Les règles métier internes restent en français — seul le contenu
 * produit pour l'utilisateur suit la locale de l'interface.
 */
function outputLanguageDirective(locale: string = "fr"): string {
  const lang = LANGUAGE_NAMES[(locale || "fr").slice(0, 2)] ?? "French";
  return `⚠️ OUTPUT LANGUAGE: Generate ALL content (title, description, options) exclusively in ${lang}. Do not mix languages.`;
}

/**
 * Prompt d'édition d'un sondage existant.
 *
 * Reçoit jusqu'à trois contextes que le LLM combine pour appliquer une
 * modification en préservant l'intention initiale de l'utilisateur :
 *
 *  - `modificationRequest` : la nouvelle demande de modification.
 *  - `currentPoll`         : sondage actuel (objet minimal OU JSON sérialisé
 *                            pour compat. avec les anciens call-sites).
 *  - `initialPrompt`       : optionnel — la demande qui a généré la
 *                            proposition courante. Permet de désambiguïser
 *                            "le dernier jeudi" / "plutôt en soirée" / etc.
 *                            en gardant le cadre conceptuel.
 *
 * Cf. spec : Docs/SPECS/2026-05-12-spec-modifications-proposition-sondage.md
 */
export function buildPollEditPrompt(
  modificationRequest: string,
  currentPoll:
    | string
    | {
        title: string;
        description?: string;
        type?: string;
        dates?: string[];
        timeSlots?: unknown[];
      },
  initialPrompt?: string | null,
  locale: string = "fr",
  pollType: "date" | "form" = "date",
): string {
  const today = new Date();
  const todayStr = getTodayLocal();
  const todayDayName = today.toLocaleDateString("fr-FR", { weekday: "long" });

  const pollJson =
    typeof currentPoll === "string"
      ? currentPoll
      : JSON.stringify(
          {
            title: currentPoll.title,
            description: currentPoll.description,
            type: currentPoll.type ?? "date",
            dates: currentPoll.dates ?? [],
            timeSlots: currentPoll.timeSlots ?? [],
          },
          null,
          JSON_INDENT,
        );

  const initialPromptSection = initialPrompt
    ? `DEMANDE INITIALE (qui a généré la proposition ci-dessous) :
"${initialPrompt}"

`
    : "";

  // Édition de FORMULAIRE (questionnaire) : schéma questions, pas de dates.
  if (pollType === "form") {
    return `Tu es l'IA DooDates. L'utilisateur veut modifier un formulaire (questionnaire) existant.
${outputLanguageDirective(locale)}

${initialPromptSection}PROPOSITION ACTUELLE (formulaire COMPLET) :
${pollJson}

NOUVELLE DEMANDE DE MODIFICATION :
"${modificationRequest}"

INSTRUCTIONS :
- Retourne le formulaire COMPLET modifié dans le MÊME schéma JSON (title, description, questions[], type:"form").
- ⚠️ CONSERVE TOUTES les questions existantes à l'identique, SAUF celles explicitement visées par la demande. N'invente PAS un nouveau formulaire, ne supprime PAS les questions non concernées.
- "ajoute une question ..." → AJOUTE une question à la fin de la liste existante en conservant toutes les autres.
- "retire / supprime la question ..." → supprime UNIQUEMENT la question visée, garde les autres.
- "change / modifie la question ..." → modifie UNIQUEMENT la question visée.
- "renomme le formulaire ..." → change seulement le title.
- CHOIX DU TYPE pour une question ajoutée : applique les mêmes règles que la génération (note/échelle → "rating" ; recommandation/NPS → "nps" ; texte long → "long-text" ; âge/nombre/email → "text" + validationType ; choix → "single"/"multiple" avec options OBLIGATOIRES). Un single/multiple SANS options est INTERDIT : utilise "text" si pas d'options évidentes (ex : "âge" → text + validationType:"number").
- Ne change PAS le titre sauf si la demande le concerne explicitement.
- Si la demande sort du cadre d'un formulaire (changement de type formulaire→sondage de dates, ajout d'image, etc.), réponds avec un JSON { "error": "OUT_OF_SCOPE", "message": "<explication courte>" }.

FORMAT JSON OBLIGATOIRE :
{
  "title": "...",
  "description": "...",
  "type": "form",
  "questions": [
    {
      "title": "...",
      "type": "single" | "multiple" | "text" | "long-text" | "rating" | "nps" | "matrix" | "date",
      "required": true | false,
      "options": ["..."],
      "ratingScale": 5,
      "ratingStyle": "stars",
      "validationType": "number"
    }
  ]
}

Réponds UNIQUEMENT avec le JSON valide, sans markdown ni texte autour.
`;
  }

  return `Tu es l'IA DooDates. L'utilisateur veut modifier un sondage existant.
${outputLanguageDirective(locale)}

${initialPromptSection}PROPOSITION ACTUELLE :
${pollJson}

NOUVELLE DEMANDE DE MODIFICATION :
"${modificationRequest}"

Aujourd'hui : ${todayStr} (${todayDayName})

INSTRUCTIONS :
- Retourne le sondage COMPLET modifié dans le MÊME schéma JSON (mêmes clés, mêmes types).
- Applique UNIQUEMENT la nouvelle demande. Conserve tout le reste de la proposition à l'identique (titre, description, dates non concernées, créneaux non concernés).
- Utilise la demande initiale comme cadre conceptuel pour interpréter la nouvelle demande :
  - "retire le dernier jeudi" → identifie la DERNIÈRE occurrence de jeudi DANS la liste de dates actuelle et supprime-la (pas un jeudi quelconque, pas un nouveau sondage).
  - "plutôt en soirée" / "plutôt le matin" → remplace les créneaux existants par des créneaux du soir/matin sur les MÊMES dates.
  - "non pas vendredi plutôt jeudi" → supprime le vendredi présent, ajoute un jeudi cohérent (même semaine, même créneau).
- Ne change PAS le titre, sauf si la demande le concerne explicitement.
- Dates futures uniquement (>= ${todayStr}).
- Si la demande sort du cadre (changement de type sondage→formulaire, ajout d'image, etc.), réponds avec un JSON { "error": "OUT_OF_SCOPE", "message": "<explication courte>" } au lieu du sondage.

FORMAT JSON OBLIGATOIRE :
{
  "title": "...",
  "description": "...",
  "type": "date",
  "dates": ["YYYY-MM-DD"],
  "timeSlots": [{"start": "HH:MM", "end": "HH:MM", "dates": ["YYYY-MM-DD"]}]
}

Réponds UNIQUEMENT avec le JSON valide, sans markdown ni texte autour.
`;
}

/**
 * Prompt SIMPLIFIÉ pour le mode direct (test A/B)
 * Sans pré-processing temporel, Gemini gère tout seul
 */
export function buildDirectPrompt(
  userInput: string,
  locale: string = "fr",
): string {
  const today = new Date();
  const todayStr = getTodayLocal();
  const todayDayName = today.toLocaleDateString("fr-FR", { weekday: "long" });

  return `${outputLanguageDirective(locale)}

Tu es l'IA DooDates, expert en planification temporelle.
IMPORTANT: Respond exclusively in the output language above. All generated content (title, description, options) must be in that language only.

Demande: "${userInput}"

Aujourd'hui: ${todayStr} (${todayDayName})
Demain: ${formatDateLocal(new Date(today.getTime() + 24 * 60 * 60 * 1000))}

RÈGLES:
1. Dates FUTURES uniquement (>= ${todayStr})
2. Si "créneau" mentionné → générer des timeSlots
3. Si heure/plage horaire mentionnée → générer des timeSlots

⚠️⚠️⚠️ RÈGLE CRITIQUE - GÉNÉRATION DES CRÉNEAUX ⚠️⚠️⚠️
DURÉE DONNÉE ("45mn", "1h", "1h30", "2h") = des créneaux RÉELS de cette durée,
CONSÉCUTIFS, qui pavent la plage (tiling). Le reliquat de fin (< durée) est JETÉ.
- "45mn entre 12h et 14h" → 12:00-12:45, 12:45-13:30 (reliquat 30min jeté ; PAS 13:30-14:15)
- "1h entre 9h et 12h" → 09:00-10:00, 10:00-11:00, 11:00-12:00
- "2h entre 14h et 17h" → 14:00-16:00 (reliquat 1h jeté)
- INTERDIT : créneaux qui se CHEVAUCHENT ou GLISSENT (ex 12:00-12:45, 12:15-13:00…). Ils doivent être bord-à-bord.

AUCUNE DURÉE → 1 SEUL timeSlot couvrant TOUTE la plage.
- "entre 12h et 14h" (sans durée, ex déjeuner) → 1 timeSlot {start:"12:00", end:"14:00"}

BORNES OUVERTES :
- "avant Xh" → début à 09:00 (start rond), fin à Xh. Avec durée → paver de 09:00 à Xh.
- "après Xh" → début à Xh, fin à X+2h. Avec durée → paver de Xh à X+2h.
- durée SANS plage ("appel 45mn") → 1 timeSlot de cette durée à 09:00 par date.

⚠️ RÈGLE CRITIQUE - JOURS DE LA SEMAINE:
Si un jour spécifique est mentionné (lundi, mardi, mercredi, jeudi, vendredi, samedi, dimanche):
→ OBLIGATOIRE: Générer UNIQUEMENT des dates correspondant à ce jour
→ INTERDIT: Générer d'autres jours de la semaine
→ EXEMPLE: "jeudi soir" → ne générer QUE des jeudis

FORMAT JSON (OBLIGATOIRE):
  "title": "Titre spécifique (ex: 'Déjeuner équipe', 'Dîner chez Romain')",
  "description": "Description optionnelle",
  "type": "date",
  "dates": ["YYYY-MM-DD"],  // ⚠️ OBLIGATOIRE - Liste des dates au niveau racine
  "timeSlots": [{"start": "HH:MM", "end": "HH:MM", "dates": ["YYYY-MM-DD"]}]
}

RÈGLE TITRE: Le titre doit être créatif et spécifique à l'événement mentionné. ÉVITER les titres génériques comme "Sondage", "Réunion" ou "Disponibilités" sauf si rien d'autre n'est précisé.

⚠️ RÈGLE CRITIQUE: Le champ "dates" est OBLIGATOIRE au niveau racine, même si timeSlots existe.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
EXEMPLE COMPLET (à imiter EXACTEMENT — pavage par la durée) :

Demande : "call 45mn, Vendredi 5 juin entre 12h et 14h, Mardi 9 juin entre 12h et 14h, Vendredi 12 juin avant 11h, Mardi 16 juin avant 11h"

✅ RÉPONSE CORRECTE (créneaux de 45mn consécutifs bord-à-bord, reliquat jeté) :
{
  "title": "Appel de suivi",
  "type": "date",
  "dates": ["2026-06-05", "2026-06-09", "2026-06-12", "2026-06-16"],
  "timeSlots": [
    {"start": "12:00", "end": "12:45", "dates": ["2026-06-05", "2026-06-09"]},
    {"start": "12:45", "end": "13:30", "dates": ["2026-06-05", "2026-06-09"]},
    {"start": "09:00", "end": "09:45", "dates": ["2026-06-12", "2026-06-16"]},
    {"start": "09:45", "end": "10:30", "dates": ["2026-06-12", "2026-06-16"]}
  ]
}
(12h-14h = 120min → 2 créneaux de 45mn, reliquat 30min jeté. "avant 11h" → début 09:00.)

❌ RÉPONSE INTERDITE (créneaux glissants/chevauchants de 15 en 15) :
{
  "timeSlots": [
    {"start": "12:00", "end": "12:45", ...},
    {"start": "12:15", "end": "13:00", ...},
    {"start": "12:30", "end": "13:15", ...}
  ]
}
Les créneaux doivent être BORD-À-BORD (12:00-12:45 puis 12:45-13:30), jamais chevauchants.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Répondre UNIQUEMENT avec le JSON valide.
`;
}

/**
 * Construit le prompt principal pour la génération de sondages
 * Extrait de gemini.ts pour modularité
 */
export function buildPollGenerationPrompt(
  userInput: string,
  dateHints: string = "",
  locale: string = "fr",
): string {
  const contextualHints = buildContextualHints(userInput);
  const today = new Date();

  // Détecter contexte repas + date spécifique
  // const isMealContext = /(déjeuner|dîner|brunch|lunch|repas)/i.test(userInput);
  // const isSpecificDateInInput =
  //   /(demain|aujourd'hui|lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche|dans \d+ jours?)/i.test(
  //     userInput,
  //   );

  // Les INSTRUCTIONS STRICTES (dateHints) PRIMENT sur les règles générales
  return `Tu es l'IA DooDates, expert en planification temporelle.
${outputLanguageDirective(locale)}
${dateHints}
${contextualHints}

Demande: "${userInput}"

RÈGLES FONDAMENTALES:
- Dates futures uniquement (>= ${getTodayLocal()})
- Calculer à partir d'aujourd'hui (${getTodayLocal()})

⚠️ NOTE: Si des INSTRUCTIONS STRICTES SUR LES DATES sont présentes ci-dessus, elles PRIMENT sur les règles ci-dessous.

PRIORITÉ #1 - SPÉCIFICITÉ DE LA DEMANDE:
- Date très spécifique → 1 DATE PRINCIPALE
- Période vague → 5-7 dates

PRIORITÉ #2 - CRÉNEAUX HORAIRES:
Générer timeSlots UNIQUEMENT si mentionné :
- Heures précises ("9h", "14h30")
- Plages horaires ("matin", "après-midi", "soir", "midi")
- Mots-clés repas ("déjeuner", "dîner", "brunch")
- Durées ("1h", "30 minutes")

⚠️⚠️⚠️ RÈGLE ABSOLUE - REPAS + DATE SPÉCIFIQUE ⚠️⚠️⚠️
Si REPAS et DATE SPÉCIFIQUE :
→ 1 DATE UNIQUEMENT
→ 1 CRÉNEAU UNIQUEMENT autour de l'heure du repas

EXCEPTION À LA RÈGLE ABSOLUE :
Si la demande contient "ou" :
→ Générer TOUTES les options mentionnées

⚠️⚠️⚠️ PLAGES EXPLICITES (PRIORITÉ HAUTE - PRIME SUR LES CONSEILS CI-DESSOUS) ⚠️⚠️⚠️
Si la demande donne une plage ou une heure PRÉCISE, émettre 1 SEUL créneau couvrant
la plage demandée. NE JAMAIS fragmenter une plage explicite en sous-créneaux.
- "entre Xh et Yh" → 1 créneau {start:"X:00", end:"Y:00"} (ex: "entre 12h et 14h" → {start:"12:00", end:"14:00"})
- "de Xh à Yh" → 1 créneau {start:"X:00", end:"Y:00"}
- "avant Xh" → 1 créneau borné {start:"09:00", end:"X:00"} (start rond, jamais une minute improvisée)
- "après Xh" → 1 créneau {start:"X:00", end:"X+2:00"}
- Durée explicite ("appel 45mn", "1h", "30 minutes") à une heure donnée → 1 créneau de cette durée
  (ex: "appel 45mn à 14h" → {start:"14:00", end:"14:45"})
→ INTERDIT de découper une plage explicite en granules de 15/30min.

CRÉNEAUX PAR TYPE D'ÉVÉNEMENT (conseils pour demandes VAGUES seulement):
⚠️ IMPORTANT : Si REPAS + DATE SPÉCIFIQUE → Voir règle absolue ci-dessus
⚠️ Si PLAGE EXPLICITE → Voir règle ci-dessus, ces conseils ne s'appliquent pas

Pour les autres cas (demande vague, sans plage précise) :
- Déjeuners : 1 créneau (12h30-13h30) par date
- Dîners : 1 créneau (19h00-20h00) par date
- Matin : Plusieurs créneaux (8h-12h, toutes les 30min)
- Après-midi : Plusieurs créneaux (14h-17h, toutes les 30min)
- Soir : Plusieurs créneaux (18h30-21h00)

EXPRESSIONS TEMPORELLES:
- "cette semaine" = semaine actuelle
- "semaine prochaine" = semaine suivante
- "demain" = ${formatDateLocal(new Date(today.getTime() + 24 * 60 * 60 * 1000))}
- "dans X jours" = ${getTodayLocal()} + X jours
- "dans X semaines" = ${getTodayLocal()} + (X × 7) jours

EXEMPLES:
- "réunion lundi ou mardi" → type: "date", timeSlots: []
- "réunion lundi matin" → 1 date (lundi), plusieurs créneaux matin
- "déjeuner demain midi" → 1 date (demain), 1 créneau (12h00-13h00)
- "disponibilité cette semaine" → 5-7 dates, pas de créneaux

FORMAT JSON (OBLIGATOIRE):
{
  "title": "Titre spécifique (ex: 'Match de foot', 'Réunion Budget Q1')",
  "description": "Description optionnelle",
  "type": "date",
  "dates": ["YYYY-MM-DD"],  // ⚠️ OBLIGATOIRE - Toujours lister les dates ici
  "timeSlots": [{"start": "HH:MM", "end": "HH:MM", "dates": ["YYYY-MM-DD"]}]
}

RÈGLE TITRE: Le titre doit être créatif et spécifique à l'événement. ÉVITER les titres génériques comme "Sondage" ou "Planning".


IMPORTANT:
- Le champ "dates" est OBLIGATOIRE au niveau racine, même si timeSlots existe.
- Répondre UNIQUEMENT avec le JSON valide.
- Pas de markdown, pas de texte avant/après.
`;
}
